import React, { useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { formatCurrency } from '_helpers/format';
import { usePrevious } from '_hooks';
import { selectError, selectLoading } from '_store/currentTransaction/selector';
import { deleteBankTransaction } from '_store/currentTransaction/slice';
import { selectModalDataByKey, selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import Text from 'components/atomic/layouts/Text';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large}px;
`;

const StyledContent = styled.div`
  width: 100%;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const StyledConfirmActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  margin-top: ${theme.spaces.small - 2}px;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DeleteTransactionModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.transactionDelete));
  const data = useSelector(selectModalDataByKey(MODAL_KEYS.transactionDelete));
  const loading = useSelector(selectLoading);
  const prevLoading = usePrevious(loading);
  const error = useSelector(selectError);
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.transactionDelete }));
  };

  const handleConfirm = useCallback(() => {
    dispatch(
      deleteBankTransaction({
        customerId,
        productId,
        transactionId: data?.id,
      })
    );
  }, [dispatch, customerId, productId, data?.id]);

  useEffect(() => {
    if (prevLoading && !loading && !error) {
      dispatch(hideModal({ key: MODAL_KEYS.transactionDelete }));
    }
  }, [dispatch, prevLoading, loading, error]);

  return (
    <Modal
      dialogClassName="update-transaction-modal"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Delete Transaction
        </Text>
        <StyledContent>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Type:</b> {data[FIELD_KEYS.type]}
          </Text>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Date:</b> {data[FIELD_KEYS.date]}
          </Text>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Category:</b> {data[FIELD_KEYS.accountName]}
          </Text>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Contact Name:</b> {data[FIELD_KEYS.contactName]}
          </Text>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Reference:</b> {data[FIELD_KEYS.reference]}
          </Text>
          <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} bottomSpace={theme.spaces.medium}>
            <b>Total:</b> {formatCurrency({ maximumFractionDigits: 2 })(data[FIELD_KEYS.total])}
          </Text>
        </StyledContent>
        <StyledConfirmActionContainer>
          <Button color={theme.colors.gray.secondary} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button color={theme.colors.red.primary} disabled={loading} onClick={handleConfirm}>
            Delete
          </Button>
          {loading && (
            <StyledSpinnerContainer>
              <Spinner color={theme.colors.indigo.superLight} />
            </StyledSpinnerContainer>
          )}
        </StyledConfirmActionContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default DeleteTransactionModal;
